import { useEffect, useRef, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Protocol } from 'pmtiles';
import { layers, LIGHT, DARK, GRAYSCALE } from '@protomaps/basemaps';
import { useHydroStore } from '../../hooks/useHydroStore';

// Register PMTiles protocol once at module level
const protocol = new Protocol();
maplibregl.addProtocol('pmtiles', protocol.tile);

const TILES_BASE = import.meta.env.BASE_URL + 'tiles';
const SOURCE_NAME = 'protomaps';

const FLAVORS = {
  streets: { theme: LIGHT, sprite: 'light', hillshade: 0.25 },
  satellite: { theme: DARK, sprite: 'dark', hillshade: 0.3 },
  terrain: { theme: GRAYSCALE, sprite: 'grayscale', hillshade: 0.5 },
} as const;

type MapMode = keyof typeof FLAVORS;

function buildStyle(mode: MapMode): maplibregl.StyleSpecification {
  const { theme, sprite, hillshade: exaggeration } = FLAVORS[mode];
  const baseLayers = layers(SOURCE_NAME, theme, { lang: 'en' }) as maplibregl.LayerSpecification[];

  // Insert hillshade after the first (background) layer
  const hillshadeLayer: maplibregl.LayerSpecification = {
    id: 'hillshade',
    type: 'hillshade',
    source: 'terrain',
    paint: {
      'hillshade-shadow-color': '#473B24',
      'hillshade-illumination-anchor': 'map',
      'hillshade-exaggeration': exaggeration,
    },
  };

  return {
    version: 8,
    glyphs: `${TILES_BASE}/fonts/{fontstack}/{range}.pbf`,
    sprite: `${TILES_BASE}/sprites/v4/${sprite}`,
    sources: {
      [SOURCE_NAME]: {
        type: 'vector',
        url: `pmtiles://${TILES_BASE}/nm-basemap.pmtiles`,
        attribution: '&copy; <a href="https://openstreetmap.org">OpenStreetMap</a>',
      },
      terrain: {
        type: 'raster-dem',
        url: `pmtiles://${TILES_BASE}/nm-terrain.pmtiles`,
        encoding: 'terrarium',
      },
    },
    layers: [baseLayers[0], hillshadeLayer, ...baseLayers.slice(1)],
  };
}

const NM_CENTER: [number, number] = [-106.0, 34.5];

export default function Map() {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const marker = useRef<maplibregl.Marker | null>(null);

  const {
    selectedPoint,
    setSelectedPoint,
    analysisResult,
    mapStyle,
  } = useHydroStore();

  const addWatershedLayers = useCallback(() => {
    if (!map.current) return;

    map.current.addSource('watershed', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });

    map.current.addLayer({
      id: 'watershed-fill',
      type: 'fill',
      source: 'watershed',
      paint: { 'fill-color': '#2563eb', 'fill-opacity': 0.2 },
    });

    map.current.addLayer({
      id: 'watershed-outline',
      type: 'line',
      source: 'watershed',
      paint: { 'line-color': '#1d4ed8', 'line-width': 2 },
    });

    // Restore watershed data if we have analysis results
    if (analysisResult?.watershed?.geometry) {
      const source = map.current.getSource('watershed') as maplibregl.GeoJSONSource;
      source?.setData({
        type: 'FeatureCollection',
        features: [analysisResult.watershed.geometry],
      });
    }
  }, [analysisResult]);

  const handleMapClick = useCallback(
    (e: maplibregl.MapMouseEvent) => {
      const { lng, lat } = e.lngLat;

      if (lng < -109.1 || lng > -102.9 || lat < 31.2 || lat > 37.1) {
        console.warn('Click outside New Mexico bounds');
        return;
      }

      setSelectedPoint({ lng, lat });
    },
    [setSelectedPoint]
  );

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: buildStyle(mapStyle),
      center: NM_CENTER,
      zoom: 6,
      maxBounds: [[-111, 29], [-101, 39]],
    });

    map.current.addControl(new maplibregl.NavigationControl(), 'top-right');
    map.current.addControl(
      new maplibregl.ScaleControl({ maxWidth: 200, unit: 'imperial' }),
      'bottom-left'
    );

    map.current.on('click', handleMapClick);
    map.current.on('load', addWatershedLayers);

    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, []);

  // Update marker when selected point changes
  useEffect(() => {
    if (!map.current) return;

    if (marker.current) {
      marker.current.remove();
      marker.current = null;
    }

    if (selectedPoint) {
      marker.current = new maplibregl.Marker({ color: '#ef4444' })
        .setLngLat([selectedPoint.lng, selectedPoint.lat])
        .addTo(map.current);
    }
  }, [selectedPoint]);

  // Update watershed polygon when analysis result changes
  useEffect(() => {
    if (!map.current) return;

    const source = map.current.getSource('watershed') as maplibregl.GeoJSONSource;
    if (!source) return;

    if (analysisResult?.watershed?.geometry) {
      source.setData({
        type: 'FeatureCollection',
        features: [analysisResult.watershed.geometry],
      });

      const bounds = new maplibregl.LngLatBounds();
      const coords = analysisResult.watershed.geometry.geometry.coordinates[0];
      coords.forEach((coord: number[]) => {
        bounds.extend([coord[0], coord[1]]);
      });
      map.current.fitBounds(bounds, { padding: 50 });
    } else {
      source.setData({
        type: 'FeatureCollection',
        features: [],
      });
    }
  }, [analysisResult]);

  // Update map style — re-add watershed layers after style swap
  useEffect(() => {
    if (!map.current) return;
    map.current.setStyle(buildStyle(mapStyle));
    map.current.once('style.load', addWatershedLayers);
  }, [mapStyle]);

  return (
    <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
  );
}
